import { useCallback, useMemo, useRef, useState } from 'react';

import { keyBy, sortBy } from 'lodash';

import {
  EntertainerQuoteReview,
  ScheduleEntertainerQuotePayload,
} from '@admin/queries/AdminEntertainerQuotesQueries';

import { uuid } from '@utilities/uuid';

import Button from '@components/buttons/Button';
import IconButton from '@components/buttons/IconButton';
import { FontAwesomeIcon } from '@components/icons/FontAwesomeIcon';
import { HorizontalScroll } from '@components/layout/HorizontalScroll/HorizontalScroll';
import { Popup } from '@components/layout/Popup/Popup';
import { TitleToolbar } from '@components/layout/TitleToolbar/TitleToolbar';

import {
  DEFAULT_QUOTE_FONT_SIZE,
  QuotePreviewContent,
  QuotePreviewUrlFn,
} from './EntertainerQuotePreviewPopup';

interface EntertainerQuoteCarouselPreviewPopupContentProps<QuoteSource> {
  reviews: EntertainerQuoteReview<QuoteSource>[];
  previewUrl: QuotePreviewUrlFn;
  onClose: () => void;
  onSchedule: (payload: ScheduleEntertainerQuotePayload) => Promise<void>;
}

interface EntertainerQuoteCarouselPreviewPopupProps<
  QuoteSource,
> extends EntertainerQuoteCarouselPreviewPopupContentProps<QuoteSource> {
  isVisible: boolean;
}

const CarouselSlide = <QuoteSource extends unknown>({
  review,
  slideNumber,
  slideCount,
  previewUrl,
  quoteFontSize,
  onQuoteFontSizeChange,
  onMove,
}: {
  review: EntertainerQuoteReview<QuoteSource> & { quoteId: string };
  slideNumber: number;
  slideCount: number;
  previewUrl: QuotePreviewUrlFn;
  quoteFontSize: number;
  onQuoteFontSizeChange: (quoteId: string, quoteFontSize: number) => void;
  onMove: (quoteId: string, direction: -1 | 1) => void;
}) => {
  const { quoteId } = review;
  const handleQuoteFontSizeChange = useCallback(
    (fontSize: number) => onQuoteFontSizeChange(quoteId, fontSize),
    [quoteId, onQuoteFontSizeChange],
  );

  return (
    <div>
      <TitleToolbar
        text={`Slide ${slideNumber} of ${slideCount}`}
        size="sm"
        action={
          <div className="d-flex gap-1">
            <IconButton
              variant="flat"
              color="neutral"
              size="sm"
              disabled={slideNumber === 1}
              onClick={() => onMove(quoteId, -1)}
            >
              <FontAwesomeIcon name="chevron-left" />
            </IconButton>
            <IconButton
              variant="flat"
              color="neutral"
              size="sm"
              disabled={slideNumber === slideCount}
              onClick={() => onMove(quoteId, 1)}
            >
              <FontAwesomeIcon name="chevron-right" />
            </IconButton>
          </div>
        }
      />
      <QuotePreviewContent
        review={review}
        previewUrl={previewUrl}
        quoteFontSize={quoteFontSize}
        onQuoteFontSizeChange={handleQuoteFontSizeChange}
      />
    </div>
  );
};

const PopupContent = <QuoteSource extends unknown>({
  reviews,
  previewUrl,
  onClose,
  onSchedule,
}: EntertainerQuoteCarouselPreviewPopupContentProps<QuoteSource>) => {
  // PopupContent only mounts while the popup is visible, so each opening gets a fresh key.
  const carouselKey = useRef(uuid());
  const [fontSizesByQuoteId, setFontSizesByQuoteId] = useState<Record<string, number>>({});
  const [isSchedulingCarousel, setIsSchedulingCarousel] = useState(false);
  const [error, setError] = useState<string>();

  const schedulableReviews = useMemo(
    () =>
      reviews.filter(
        (review): review is EntertainerQuoteReview<QuoteSource> & { quoteId: string } =>
          Boolean(review.quoteId),
      ),
    [reviews],
  );

  // Start from any previously saved carouselOrder; unordered quotes go last in their given order.
  const [orderedQuoteIds, setOrderedQuoteIds] = useState<string[]>(() =>
    sortBy(schedulableReviews, (review) => review.curatedQuote?.carouselOrder ?? Infinity).map(
      (review) => review.quoteId,
    ),
  );

  const reviewsToSchedule = useMemo(() => {
    const reviewsByQuoteId = keyBy(schedulableReviews, 'quoteId');
    const ordered = orderedQuoteIds.map((quoteId) => reviewsByQuoteId[quoteId]).filter(Boolean);
    // Append any reviews that arrived after the popup opened
    const unordered = schedulableReviews.filter(
      (review) => !orderedQuoteIds.includes(review.quoteId),
    );
    return [...ordered, ...unordered];
  }, [schedulableReviews, orderedQuoteIds]);

  const handleMove = useCallback(
    (quoteId: string, direction: -1 | 1) => {
      setOrderedQuoteIds(() => {
        const next = reviewsToSchedule.map((review) => review.quoteId);
        const from = next.indexOf(quoteId);
        const to = from + direction;
        if (from === -1 || to < 0 || to >= next.length) {
          return next;
        }
        [next[from], next[to]] = [next[to], next[from]];
        return next;
      });
    },
    [reviewsToSchedule],
  );

  // Local edits win, then the saved size from the query, then the default
  const getQuoteFontSize = (review: EntertainerQuoteReview<QuoteSource> & { quoteId: string }) =>
    fontSizesByQuoteId[review.quoteId] ??
    review.curatedQuote?.quoteFontSize ??
    DEFAULT_QUOTE_FONT_SIZE;

  const handleQuoteFontSizeChange = useCallback((quoteId: string, quoteFontSize: number) => {
    setFontSizesByQuoteId((prev) => ({ ...prev, [quoteId]: quoteFontSize }));
  }, []);

  const handleScheduleCarousel = async () => {
    setError(undefined);
    setIsSchedulingCarousel(true);

    try {
      for (let carouselOrder = 0; carouselOrder < reviewsToSchedule.length; carouselOrder++) {
        const review = reviewsToSchedule[carouselOrder];
        await onSchedule({
          quoteId: review.quoteId,
          quoteFontSize: getQuoteFontSize(review),
          carouselKey: carouselKey.current,
          carouselOrder,
        });
      }
      onClose();
    } catch (e: any) {
      console.error(e);
      setError(e?.message ?? 'Failed to schedule carousel.');
    } finally {
      setIsSchedulingCarousel(false);
    }
  };

  return (
    <>
      <HorizontalScroll
        items={[
          <div className="d-flex gap-2">
            {reviewsToSchedule.map((review, i) => (
              <div key={review.quoteId} style={{ width: 300 }}>
                <CarouselSlide
                  review={review}
                  slideNumber={i + 1}
                  slideCount={reviewsToSchedule.length}
                  previewUrl={previewUrl}
                  quoteFontSize={getQuoteFontSize(review)}
                  onQuoteFontSizeChange={handleQuoteFontSizeChange}
                  onMove={handleMove}
                />
              </div>
            ))}
          </div>,
        ]}
      />

      {error && <p className="text-danger mt-1 mb-0">{error}</p>}

      <div className="mt-4">
        <Button
          disabled={isSchedulingCarousel || !reviewsToSchedule.length}
          onClick={handleScheduleCarousel}
        >
          {`Schedule Carousel (${reviewsToSchedule.length})`}
        </Button>
      </div>
    </>
  );
};

export const EntertainerQuoteCarouselPreviewPopup = <QuoteSource extends unknown>({
  isVisible,
  ...props
}: EntertainerQuoteCarouselPreviewPopupProps<QuoteSource>) => {
  return (
    <Popup isVisible={isVisible} onClose={props.onClose} height="full" title="Preview Carousel">
      <Popup.Content>{isVisible && <PopupContent {...props} />}</Popup.Content>
    </Popup>
  );
};
